import { isNativePlatform } from '../platform';
import { getCacheOwner, isReadAllowed, setCacheOwner } from './meta';
import { ALL_TABLE_NAMES, ensureSchema, quoteIdentifier } from './schema';

import type { OfflineDbDriver, OfflineDbExecutor } from './driver-types';

/**
 * Lifecycle owner for the encrypted offline cache.
 *
 * A module-scoped manager rather than a React provider: its most important
 * callers (`clearAllAppData`, the circuit-breaker teardown) are non-React
 * modules on the sign-out paths. Import hygiene matters here - files in
 * offline-db/ import `lib/mobile` siblings DIRECTLY, never the `@/lib/mobile`
 * barrel, whose `clearAllAppData` re-export would close an import cycle.
 *
 * Server-derived families are disposable, while local draft rows are
 * user-authored recovery snapshots. Known schema versions migrate forward;
 * unrecoverable open/cipher failures still resolve to wipe and rebuild rather
 * than exposing data under an uncertain key or owner.
 *
 * Key custody (decision recorded in the offline plan): the SQLite plugin owns
 * the passphrase in its own Keychain entry. One copy, one owner - a second
 * copy in our secure storage would double the secret's surface for no
 * capability. Fresh random key per sign-in: the wipe crypto-erases the
 * passphrase, and the next `getOfflineDb()` after sign-in mints a new one.
 */
const DB_NAME = 'pp-offline-cache';
const DB_VERSION = 1;

const defaultDriverFactory = async (): Promise<OfflineDbDriver> => {
  const { createCapacitorDriver } = await import('./capacitor-driver');
  return createCapacitorDriver();
};

let activeDriver: OfflineDbDriver | null = null;
let activeExecutor: OfflineDbExecutor | null = null;
let activeOwner: string | null = null;
let opening: Promise<OfflineDbExecutor | null> | null = null;
let openingOwner: string | null = null;
let wiping: Promise<void> | null = null;
let driverFactory = defaultDriverFactory;

// Every table whose rows carry cached constituent data. `meta` and
// `sync_state` are bookkeeping: the sync layer must keep reading them while
// locked, or it could never evaluate and refresh the verification stamp.
// Regex LITERALS (not constructed) so the security lint can verify them,
// and linear-scan safe: literal alternation, single bounded quantifier.
// Case-insensitive - SQL keywords and identifiers match in any casing, so a
// lowercase `from` cannot slip past the gate. The connection.test drift
// guard pins the table list to ALL_TABLE_NAMES.
// Quoted spellings are handled by normalization in isGatedPayloadRead;
// schema-qualified spellings (`FROM main.email_bodies`) by the dot-prefixed
// companion below. Split this way, every expression stays provably linear
// (at most one quantifier, literal alternation).
const PAYLOAD_TABLE_READ =
  /\bfrom\s+(?:bootstrap_profile|org_memberships|email_accounts_meta|email_index|email_bodies|tasks|notifications|local_drafts)\b/i;
// Matching ANY dot-prefixed payload-table name over-gates a little (an
// alias-qualified column sharing a table's name would match) - over-gating
// is the fail-closed direction here.
const QUALIFIED_PAYLOAD_TABLE_READ =
  /\.(?:bootstrap_profile|org_memberships|email_accounts_meta|email_index|email_bodies|tasks|notifications|local_drafts)\b/i;
// The data that must not leave a locked cache is the payload column (or a
// star select, which would include it - COUNT(*) matching too is fail-closed
// in the right direction). Accounting reads over ids/bytes stay open so
// eviction keeps working while locked.
const PAYLOAD_COLUMN_READ = /\bpayload\b|\*/i;

/** Whether a statement reads payload content from a gated table (R15). */
export function isGatedPayloadRead(statement: string): boolean {
  // Strip identifier quoting (`FROM "email_bodies"`, backticks, brackets)
  // so a quoted spelling cannot accidentally slip the gate.
  const normalized = statement.replaceAll(/["'[\]`]/g, '');
  const readsPayloadTable =
    PAYLOAD_TABLE_READ.test(normalized) || QUALIFIED_PAYLOAD_TABLE_READ.test(normalized);
  return readsPayloadTable && PAYLOAD_COLUMN_READ.test(normalized);
}

/**
 * The R15 read lock's SECOND layer (defense-in-depth): the executor handed
 * to consumers refuses payload reads once the verification stamp goes stale,
 * so a hydration code path that forgets its own check still cannot read
 * cached constituent data on a revoked offline device. Locked reads degrade
 * to a cache miss (empty result), matching the hydration contract. Writes
 * and accounting reads (ids, byte counts) stay open - resyncing is how a
 * device becomes verified again, and eviction keeps working while locked.
 */
function gateExecutor(driver: OfflineDbDriver): OfflineDbExecutor {
  return {
    run: async (statement, values) => await driver.run(statement, values),
    runBatch: async (statements) => await driver.runBatch(statements),
    query: async <T>(statement: string, values?: readonly unknown[]): Promise<T[]> => {
      if (isGatedPayloadRead(statement) && !(await isReadAllowed(driver, Date.now()))) {
        return [];
      }
      return await driver.query<T>(statement, values);
    },
  };
}

function generateDatabaseKey(): string {
  // 256 bits, hex-encoded. crypto.getRandomValues is available in WKWebView.
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Crypto-erase, with a visible fallback: the key is THE security control
 * (ciphertext without it is inert), so its failure must be neither silent
 * nor final. When the connection is still open, shred the plaintext through
 * it before the close; either way, surface a status-only signal - no paths,
 * no identifiers.
 */
async function cryptoEraseOrShred(driver: OfflineDbDriver, isOpen: boolean): Promise<void> {
  try {
    await driver.clearEncryptionSecret();
  } catch (error) {
    console.error('offline_db.crypto_erase_failed', {
      error: error instanceof Error ? error.name : 'unknown',
    });
    if (isOpen) {
      for (const table of ALL_TABLE_NAMES) {
        await driver.run(`DROP TABLE IF EXISTS ${quoteIdentifier(table)}`).catch(() => null);
      }
    }
  }
}

/**
 * A post-open failure must close the connection it opened before the error
 * propagates: the recovery path runs on a DIFFERENT driver instance, whose
 * close cannot reach this one, and a leaked live handle wedges the plugin's
 * native registry ("Connection already exists") and blocks file deletes.
 */
async function prepareOrClose<T>(driver: OfflineDbDriver, prepare: () => Promise<T>): Promise<T> {
  try {
    return await prepare();
  } catch (error) {
    await driver.close(DB_NAME).catch(() => null);
    throw error;
  }
}

async function openOnce(ownerUserId: string): Promise<OfflineDbDriver> {
  const driver = await driverFactory();
  if (!(await driver.isSecretStored())) {
    await driver.setEncryptionSecret(generateDatabaseKey());
  }
  await driver.open(DB_NAME, DB_VERSION);
  const owner = await prepareOrClose(driver, async () => {
    await ensureSchema(driver);
    return await getCacheOwner(driver);
  });

  // Self-enforcing account-change teardown: the database validates whose it
  // is on every open and destroys itself on mismatch, instead of trusting
  // every auth flow to remember to drop it. A different user signing in on
  // this device MUST NOT be able to read the previous user's cache.
  if (owner !== null && owner !== ownerUserId) {
    // Inline destroy, NOT wipeOfflineDb(): that function awaits any in-flight
    // open, and this code runs INSIDE the open - calling it here deadlocks.
    // Same order, same reasoning: key first, then close, then the file.
    await cryptoEraseOrShred(driver, true);
    await driver.close(DB_NAME).catch(() => null);
    await driver.deleteDatabase(DB_NAME).catch(() => null);

    const fresh = await driverFactory();
    await fresh.setEncryptionSecret(generateDatabaseKey());
    await fresh.open(DB_NAME, DB_VERSION);
    await prepareOrClose(fresh, async () => {
      await ensureSchema(fresh);
      await setCacheOwner(fresh, ownerUserId);
    });
    return fresh;
  }
  if (owner === null) {
    await prepareOrClose(driver, async () => await setCacheOwner(driver, ownerUserId));
  }
  return driver;
}

async function openAndPrepare(ownerUserId: string): Promise<OfflineDbDriver> {
  try {
    return await openOnce(ownerUserId);
  } catch {
    // Disposable-cache doctrine applied to the open itself: any failure here
    // resolves to destroy and rebuild, once. The load-bearing case is a
    // crash that landed between a wipe's crypto-erase and its file delete -
    // the surviving file is ciphertext under an erased key, and without this
    // recovery every future open fails the same way forever.
    const cleaner = await driverFactory();
    await cryptoEraseOrShred(cleaner, false);
    await cleaner.close(DB_NAME).catch(() => null);
    await cleaner.deleteDatabase(DB_NAME).catch(() => null);
    return await openOnce(ownerUserId);
  }
}

function isWipeEpochCurrent(expectedWipeEpoch: number | undefined): boolean {
  return expectedWipeEpoch === undefined || wipeEpoch === expectedWipeEpoch;
}

function canOpenAtWipeEpoch(expectedWipeEpoch: number | undefined): boolean {
  return isNativePlatform() && isWipeEpochCurrent(expectedWipeEpoch);
}

function hasForeignOpening(ownerUserId: string): boolean {
  return opening !== null && openingOwner !== ownerUserId;
}

async function settleForeignOpening(
  ownerUserId: string,
  foreignWaits: number,
  expectedWipeEpoch: number | undefined,
): Promise<OfflineDbExecutor | null> {
  const foreignOpening = opening;
  if (foreignOpening === null) {
    return null;
  }
  await foreignOpening.catch(() => null);
  if (foreignWaits >= 1) {
    return null;
  }
  return await openForOwner(ownerUserId, foreignWaits + 1, expectedWipeEpoch);
}

async function openForOwner(
  ownerUserId: string,
  foreignWaits: number,
  expectedWipeEpoch?: number,
): Promise<OfflineDbExecutor | null> {
  if (!canOpenAtWipeEpoch(expectedWipeEpoch)) {
    return null;
  }
  return await openAllowedOwner(ownerUserId, foreignWaits, expectedWipeEpoch);
}

async function resumeAfterWipe(
  activeWipe: Promise<void>,
  ownerUserId: string,
  foreignWaits: number,
  expectedWipeEpoch: number | undefined,
): Promise<OfflineDbExecutor | null> {
  await activeWipe.catch(() => null);
  return await openForOwner(ownerUserId, foreignWaits, expectedWipeEpoch);
}

async function openAllowedOwner(
  ownerUserId: string,
  foreignWaits: number,
  expectedWipeEpoch?: number,
): Promise<OfflineDbExecutor | null> {
  // A wipe in progress owns the database's fate. Opening around it would
  // resurrect a connection to - or recreate under a fresh key - the very
  // file it is destroying, after the kill path already reported success.
  const activeWipe = wiping;
  if (activeWipe !== null) {
    return await resumeAfterWipe(activeWipe, ownerUserId, foreignWaits, expectedWipeEpoch);
  }
  // The fast path validates ownership too (Delve finding): a live connection
  // opened for one user must never be handed to another, even within one
  // process lifetime - the kill-path wipes cover normal sign-out flows, but
  // this module defends itself rather than trusting them.
  if (activeDriver && activeOwner === ownerUserId) {
    return activeExecutor;
  }
  if (activeDriver) {
    // Different user than the live connection: drop the handle; the open
    // below revalidates against the ON-DISK owner and destroys as needed.
    await closeOfflineDb();
    if (!isWipeEpochCurrent(expectedWipeEpoch)) {
      return null;
    }
  }
  // A foreign open settles before this caller re-enters. Two sustained streams
  // fail the second wait fast instead of ping-ponging key derivation forever.
  if (hasForeignOpening(ownerUserId)) {
    return await settleForeignOpening(ownerUserId, foreignWaits, expectedWipeEpoch);
  }
  if (opening === null) {
    if (wiping !== null) {
      // A wipe started while we awaited above - re-enter behind it.
      return await openForOwner(ownerUserId, foreignWaits, expectedWipeEpoch);
    }
    openingOwner = ownerUserId;
    opening = (async () => {
      const driver = await openAndPrepare(ownerUserId);
      activeDriver = driver;
      activeOwner = ownerUserId;
      activeExecutor = gateExecutor(driver);
      return activeExecutor;
    })().finally(() => {
      opening = null;
      openingOwner = null;
    });
  }
  const executor = await opening;
  return isWipeEpochCurrent(expectedWipeEpoch) ? executor : null;
}

/**
 * Lazy-open the cache for a specific user. Returns null off native (the
 * cache does not exist on web or desktop). Single-flight: concurrent callers
 * share one open, because SQLCipher key derivation per open is the expensive
 * step. `ownerUserId` comes from the caller (the hydration layer knows the
 * session) - this module deliberately imports nothing from auth.
 *
 * Hands out {@link OfflineDbExecutor}, not the full driver: consumers get
 * statements only, never lifecycle or key custody - and payload reads are
 * gated behind the R15 verification window (see {@link gateExecutor}).
 */
export async function getOfflineDb(ownerUserId: string): Promise<OfflineDbExecutor | null> {
  return await openForOwner(ownerUserId, 0);
}

/**
 * Acquire the cache only while the caller's pre-I/O wipe generation is still
 * current. A request that resolves after sign-out must not wait behind the wipe
 * and then recreate the database under a fresh key.
 */
export async function getOfflineDbAtWipeEpoch(
  ownerUserId: string,
  expectedWipeEpoch: number,
): Promise<OfflineDbExecutor | null> {
  return await openForOwner(ownerUserId, 0, expectedWipeEpoch);
}

/**
 * Close the live connection WITHOUT destroying anything. This is not a kill
 * path: it neither touches the key nor the file, and an in-flight open will
 * happily re-establish a connection afterwards. Sign-out and teardown MUST
 * use {@link wipeOfflineDb}.
 */
export async function closeOfflineDb(): Promise<void> {
  const driver = activeDriver;
  activeDriver = null;
  activeExecutor = null;
  activeOwner = null;
  if (driver) {
    await driver.close(DB_NAME).catch(() => null);
  }
}

/**
 * Monotonic count of wipes, incremented at wipe ENTRY. Write-through paths
 * capture it BEFORE their network fetch and re-check it AFTER acquiring the
 * write cache - the post-acquisition check is the one that decides, because
 * the open path can await an in-flight wipe and then re-open. A fetch that
 * resolves after sign-out must not repopulate the destroyed database.
 * Residue past that check is benign: a wipe starting mid-write dies with
 * the deleted file, an empty re-created database holds no data, and the
 * owner-mismatch destroy on the next open remains the backstop.
 */
let wipeEpoch = 0;

export function getWipeEpoch(): number {
  return wipeEpoch;
}

/**
 * Destroy the cache: key FIRST (crypto-erase is the authoritative control -
 * ciphertext without its key is inert even if file deletion fails), then
 * close, then delete the file and its WAL/journal siblings. Best-effort per
 * step, matching `clearAllAppData`'s convention that one failed step never
 * strands the rest. MUST run from every kill path: user sign-out, the
 * circuit-breaker teardown, and account-change teardown all route here.
 */
export async function wipeOfflineDb(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }
  wipeEpoch += 1;
  if (wiping !== null) {
    // Concurrent wipes coalesce - the in-flight one's work covers both.
    await wiping;
    return;
  }
  const work = (async () => {
    // Drain in-flight opens first. New opens cannot start underneath us:
    // `openForOwner` awaits `wiping` before touching anything, and `wiping`
    // is set the moment this function's synchronous prefix completes.
    while (opening !== null) {
      await opening.catch(() => null);
    }
    let driver: OfflineDbDriver | null = activeDriver;
    const wasOpen = driver !== null;
    if (!driver) {
      try {
        driver = await driverFactory();
      } catch {
        driver = null;
      }
    }
    if (!driver) {
      return;
    }
    await cryptoEraseOrShred(driver, wasOpen);
    await closeOfflineDb();
    await driver.deleteDatabase(DB_NAME).catch(() => null);
  })();
  wiping = work;
  try {
    await work;
  } finally {
    wiping = null;
  }
}

export function __setOfflineDbDriverForTests(
  factory: (() => Promise<OfflineDbDriver>) | null,
): void {
  activeDriver = null;
  activeExecutor = null;
  activeOwner = null;
  opening = null;
  openingOwner = null;
  wiping = null;
  driverFactory = factory ?? defaultDriverFactory;
}

export async function __openForOwnerForTests(
  ownerUserId: string,
  foreignWaits: number,
): Promise<OfflineDbExecutor | null> {
  return await openForOwner(ownerUserId, foreignWaits);
}
